import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Check, Loader2, Send } from "lucide-react";
import { QUOTE_SERVICES, SURFACES, FREQUENCIES } from "@/lib/content";
import QuoteSuccess from "@/components/site/QuoteSuccess";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { services: [], surface: "", frequency: "À définir", nom: "", prenom: "", telephone: "", email: "", adresse: "", message: "" };

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    data-testid={testid}
    className={`relative inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm transition-[background-color,border-color,color,box-shadow] duration-300 ${
      active
        ? "border-cyan-300 bg-cyan-300/10 text-white shadow-[0_0_20px_rgba(10,245,240,0.2)]"
        : "border-white/15 text-slate-300 hover:border-cyan-300/50 hover:text-white"
    }`}
  >
    <span className={`flex h-4 w-4 items-center justify-center rounded-full border transition-colors duration-300 ${active ? "bg-cyan-300 border-cyan-300" : "border-white/30"}`}>
      {active && <Check className="h-3 w-3 text-[#050811]" strokeWidth={3} />}
    </span>
    {children}
  </button>
);

const Block = ({ n, title, children }) => (
  <fieldset className="border-t border-white/10 pt-7 first:border-0 first:pt-0">
    <legend className="sr-only">{title}</legend>
    <div className="mb-5 flex items-center gap-3">
      <span className="font-mono-uc text-xs text-cyan-300">{n}</span>
      <span className="font-display text-lg text-white">{title}</span>
    </div>
    {children}
  </fieldset>
);

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function QuoteForm() {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const toggle = (s) => setF((p) => ({ ...p, services: p.services.includes(s) ? p.services.filter((x) => x !== s) : [...p.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.services.length) return toast.error("Choisissez au moins un type de prestation.");
    if (!f.surface) return toast.error("Indiquez la surface à traiter.");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, f);
      setResult(data);
      if (data?.business_email_sent) toast.success("Votre demande a bien été envoyée.");
      else toast.warning("Demande enregistrée, mais l'envoi d'email a échoué. Nous la traiterons malgré tout.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Vérifiez les champs du formulaire (email, téléphone...).");
    } finally {
      setLoading(false);
    }
  };

  if (result) return <QuoteSuccess result={result} email={f.email} onReset={() => { setResult(null); setF(EMPTY); }} />;

  return (
    <form onSubmit={submit} className="relative rounded-[28px] border border-cyan-300/15 bg-[#050811]/70 backdrop-blur-xl p-6 sm:p-10 space-y-7" data-testid="quote-form">
      <Block n="A" title="Type de prestation">
        <div className="flex flex-wrap gap-2.5" data-testid="quote-step-service-type">
          {QUOTE_SERVICES.map((s) => (
            <Chip key={s} active={f.services.includes(s)} onClick={() => toggle(s)} testid={`quote-service-${slug(s)}`}>{s}</Chip>
          ))}
        </div>
      </Block>

      <Block n="B" title="Surface à traiter">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="quote-step-surface">
          {SURFACES.map((s, i) => (
            <motion.button
              type="button"
              whileTap={{ scale: 0.96 }}
              key={s}
              onClick={() => setF((p) => ({ ...p, surface: s }))}
              data-testid={`quote-surface-${i}`}
              className={`rounded-2xl border px-4 py-4 text-left transition-[background-color,border-color] duration-300 ${
                f.surface === s ? "border-cyan-300 bg-cyan-300/10" : "border-white/15 hover:border-cyan-300/50"
              }`}
            >
              <div className="font-mono-uc text-[11px] text-slate-400">Tranche 0{i + 1}</div>
              <div className={`mt-1 font-display text-lg ${f.surface === s ? "text-cyan-200" : "text-white"}`}>{s}</div>
            </motion.button>
          ))}
        </div>
      </Block>

      <Block n="C" title="Fréquence souhaitée">
        <div className="flex flex-wrap gap-2.5" data-testid="quote-step-frequency">
          {FREQUENCIES.map((s) => (
            <Chip key={s} active={f.frequency === s} onClick={() => setF((p) => ({ ...p, frequency: s }))} testid={`quote-frequency-${slug(s)}`}>{s}</Chip>
          ))}
        </div>
      </Block>

      <Block n="D" title="Vos coordonnées">
        <div className="grid sm:grid-cols-2 gap-4">
          <input required className="uc-field" placeholder="Nom *" value={f.nom} onChange={set("nom")} data-testid="quote-input-nom" />
          <input required className="uc-field" placeholder="Prénom *" value={f.prenom} onChange={set("prenom")} data-testid="quote-input-prenom" />
          <input required type="tel" minLength={6} className="uc-field" placeholder="Téléphone *" value={f.telephone} onChange={set("telephone")} data-testid="quote-input-phone" />
          <input required type="email" className="uc-field" placeholder="Email *" value={f.email} onChange={set("email")} data-testid="quote-input-email" />
          <input required minLength={3} className="uc-field sm:col-span-2" placeholder="Adresse du chantier *" value={f.adresse} onChange={set("adresse")} data-testid="quote-input-adresse" />
          <textarea rows={4} maxLength={2000} className="uc-field sm:col-span-2 resize-none" placeholder="Message / précisions (accès, horaires, contraintes...)" value={f.message} onChange={set("message")} data-testid="quote-input-message" />
        </div>
      </Block>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 pt-2">
        <p className="text-xs text-slate-400 max-w-sm leading-relaxed">
          Vos données sont utilisées uniquement pour traiter votre demande de devis. Voir les mentions légales.
        </p>
        <button
          type="submit"
          disabled={loading}
          className="group inline-flex items-center justify-center gap-3 rounded-full bg-brand px-8 py-4 font-semibold text-[#050811] hover:shadow-[0_0_48px_rgba(10,245,240,0.5)] transition-shadow duration-500 disabled:opacity-70"
          data-testid="quote-submit-button"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />}
          {loading ? "Envoi en cours..." : "Envoyer la demande"}
        </button>
      </div>
    </form>
  );
}
